import { createTheme } from '@mui/material/styles';

/**
 * The application's single global theme.
 *
 * This file is the highest-leverage place in the client: it reaches every MUI component in all
 * 375 files without editing them. Rules here must be app-wide truths — anything page-specific
 * belongs in that page's `sx`.
 *
 * Every override that changes layout is scoped below `md`, because desktop behaviour at >= 1200px
 * must be visually unchanged.
 */
export const theme = createTheme({
    // MUI defaults, stated explicitly so nobody "helpfully" invents custom values later.
    breakpoints: { values: { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536 } },

    typography: {
        // Pin to the same stack src/index.css already uses. CssBaseline sets
        // `body { font-family: Roboto, Helvetica, Arial, sans-serif }` at the same specificity,
        // and public/index.html loads no Roboto webfont, so without this the app can silently
        // fall back to Arial depending on style-injection order (seen on Linux/Windows).
        fontFamily: [
            '-apple-system',
            'BlinkMacSystemFont',
            "'Segoe UI'",
            "'Roboto'",
            "'Oxygen'",
            "'Ubuntu'",
            "'Cantarell'",
            "'Fira Sans'",
            "'Droid Sans'",
            "'Helvetica Neue'",
            'sans-serif',
        ].join(','),
    },

    components: {
        // Tab bars scroll (with arrows, also on touch) instead of clipping tabs that don't fit —
        // the statutory registers' 10 tabs were unreachable on phone and tablet.
        MuiTabs: {
            defaultProps: { variant: 'scrollable', scrollButtons: 'auto', allowScrollButtonsMobile: true },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    '@media (max-width:899.95px)': {
                        minHeight: 44,
                        // Long labels wrap rather than being clipped on a narrow screen.
                        whiteSpace: 'normal',
                        lineHeight: 1.3,
                    },
                },
            },
        },

        MuiIconButton: {
            styleOverrides: {
                root: {
                    '@media (max-width:899.95px)': { minWidth: 44, minHeight: 44 },
                },
                sizeSmall: {
                    '@media (max-width:899.95px)': { minWidth: 36, minHeight: 36 },
                },
            },
        },

        MuiInputBase: {
            styleOverrides: {
                // 16px is not cosmetic: below it, iOS Safari zooms the viewport on focus and
                // does not zoom back out, which strands the user at 2x on a form. That rationale
                // is touch-width only, and unscoped this wins over any ancestor-inherited font
                // size in all 375 files, so it is breakpoint-scoped like every other override here.
                input: { '@media (max-width:899.95px)': { fontSize: 16 } },
            },
        },

        MuiDialog: {
            styleOverrides: {
                paper: {
                    '@media (max-width:599.95px)': {
                        margin: 0,
                        width: '100%',
                        maxWidth: '100%',
                        maxHeight: '100%',
                        height: '100%',
                        borderRadius: 0,
                    },
                },
            },
        },

        MuiTableContainer: {
            styleOverrides: {
                // MUI's TableContainer already scrolls horizontally by default (overflowX: auto);
                // the load-bearing addition here is `maxWidth: '100%'`, which stops a wide table
                // from pushing the surrounding page layout wider instead of scrolling in place.
                root: {
                    overflowX: 'auto',
                    maxWidth: '100%',
                    WebkitOverflowScrolling: 'touch',
                },
            },
        },

        MuiTableCell: {
            styleOverrides: {
                root: {
                    '@media (max-width:899.95px)': { padding: '8px 10px' },
                    // Phones: keep IDs, dates, amounts and status chips on one line. Every table is
                    // inside a horizontally scrollable container (TableContainer / ScrollableTable /
                    // overflow Box), so the table scrolls instead of squeezing cells into 3-line
                    // wraps. Cells that must wrap long text opt out with sx={{ whiteSpace: 'normal' }}
                    // (component sx is emitted after theme overrides, so it wins).
                    '@media (max-width:599.95px)': { whiteSpace: 'nowrap' },
                },
            },
        },
    },
});

export default theme;
